import React from "react";
import { Link } from "react-router-dom";
import { Shield, Mail, CreditCard, Database, Cookie, Globe, Trash2, FileText, AlertCircle } from "lucide-react";
import SEO from "../components/SEO";
import { useContentApi } from "../context/ContentContext";
import { mailbox } from "../lib/mailboxes";

const Section = ({ icon: Icon, title, children, id }) => (
    <section id={id} className="scroll-mt-24" data-testid={`privacy-section-${id}`}>
        <h2 className="font-heading text-xl sm:text-2xl text-[#1C252E] font-medium flex items-center gap-2 mb-3">
            <Icon className="w-5 h-5 text-[#E53935] flex-shrink-0" strokeWidth={1.5} />
            {title}
        </h2>
        <div className="space-y-3 text-base text-[#4A5568] leading-relaxed pl-7">
            {children}
        </div>
    </section>
);

const LAST_UPDATED = "May 22, 2026";

const Privacy = () => {
    const { c } = useContentApi();
    const privacyEmail = mailbox(c, "privacy");
    return (
    <div className="bg-[#FDFBF7] min-h-[calc(100vh-160px)]" data-testid="privacy-page">
        <SEO
            title="Privacy Policy"
            description="How Reddog Community Foundation collects, uses, and protects your personal information. We never sell your data."
            path="/privacy"
        />

        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-16">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F2D8D5] rounded-full mb-4">
                <Shield className="w-3 h-3 text-[#E53935]" strokeWidth={2} />
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#E53935]">Your privacy</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight">
                Privacy Policy
            </h1>
            <p className="text-base text-[#4A5568] mt-4 leading-relaxed">
                Reddog Community Foundation ("we", "us", "the foundation") respects your privacy. This policy
                explains what data we collect, why we collect it, how we use it, and how you can control or
                delete it. Plain English — no lawyer-speak.
            </p>
            <p className="text-xs text-[#4A5568] mt-3 italic" data-testid="privacy-last-updated">Last updated: {LAST_UPDATED}</p>

            <div className="mt-10 space-y-10">
                <Section icon={Mail} title="What we collect and why" id="what-we-collect">
                    <p>We only collect the minimum information needed to run the foundation. Specifically:</p>
                    <ul className="list-disc list-outside space-y-2 ml-5">
                        <li>
                            <strong>Email address</strong> — collected when you sign up, donate, or sign in with Google or Facebook.
                            We use it to <strong>send donation receipts</strong> (required by U.S. tax law for gifts of $250 or more),
                            send foundation digests if you opt in, and to send magic-link sign-in recovery emails. We do not send
                            unsolicited marketing.
                        </li>
                        <li>
                            <strong>Name and (optionally) profile photo</strong> — used to greet you and, if you choose to
                            appear publicly on the donor wall, to display your support.
                        </li>
                        <li>
                            <strong>Donation history</strong> — amount, date, and (if you designated one) the fund or campaign.
                            This is required for accounting and to issue your year-end tax summary.
                        </li>
                        <li>
                            <strong>Payment information</strong> — when you donate, your card or bank details go directly to our
                            payment processor (Stripe and/or PayPal). <strong>We never see and never store your card number.</strong>
                            We only retain a payment processor transaction ID for our records.
                        </li>
                        <li>
                            <strong>Volunteer information</strong> — if you sign a volunteer agreement, we store the name, contact info,
                            and signed agreement document so we can comply with school-district volunteer policies.
                        </li>
                        <li>
                            <strong>Sign-in metadata</strong> — last sign-in time and which provider you used (email, Google, etc.) for
                            security and to help if you forget which method you originally signed up with.
                        </li>
                    </ul>
                </Section>

                <Section icon={CreditCard} title="Payments" id="payments">
                    <p>
                        Card payments are processed by <strong>Stripe</strong> and/or <strong>PayPal</strong>. They are
                        PCI-DSS Level 1 certified and bear all card data handling on our behalf. We see and store only:
                    </p>
                    <ul className="list-disc list-outside space-y-2 ml-5">
                        <li>The transaction ID returned by the processor</li>
                        <li>The donation amount and currency</li>
                        <li>The last 4 digits of the card and card brand (for your receipt)</li>
                        <li>Your billing email + name as you entered them at checkout</li>
                    </ul>
                    <p>
                        Refunds are issued through the same processor and follow their timing (typically 5–10 business days).
                    </p>
                </Section>

                <Section icon={Database} title="How we use your data" id="how-we-use">
                    <ul className="list-disc list-outside space-y-2 ml-5">
                        <li>To send you a tax receipt and year-end summary of giving</li>
                        <li>To track donations against restricted funds for our public ledger</li>
                        <li>To verify board / volunteer / contractor permissions when you sign in</li>
                        <li>To send <em>opt-in</em> impact updates ("here's what your donation did")</li>
                        <li>To detect and prevent fraud (e.g. brute-force login attempts)</li>
                        <li>To comply with legal requests (subpoena, court order, IRS audit)</li>
                    </ul>
                    <p className="mt-4">
                        <strong>We do NOT:</strong> sell your data, rent your data, share it with advertisers, or use it to
                        train AI models. Period.
                    </p>
                </Section>

                <Section icon={Globe} title="Third parties we share with" id="third-parties">
                    <p>
                        We share only the minimum necessary data with the following service providers, each governed by their own
                        privacy policy:
                    </p>
                    <ul className="list-disc list-outside space-y-2 ml-5">
                        <li><strong>Stripe / PayPal</strong> — to process donations</li>
                        <li><strong>Google</strong> — only if you choose "Sign in with Google" (we never read your Gmail or Drive)</li>
                        <li><strong>Facebook</strong> — only if you choose "Sign in with Facebook" (we request email + public profile only)</li>
                        <li><strong>Email delivery service</strong> (our SMTP provider) — to send receipts and magic-link sign-ins</li>
                        <li><strong>Cloud hosting</strong> — our VPS provider and MongoDB Atlas (encrypted at rest)</li>
                    </ul>
                    <p className="mt-4">
                        We have written agreements with each that prohibit them from using your data for their own purposes.
                    </p>
                </Section>

                <Section icon={Cookie} title="Cookies" id="cookies">
                    <p>
                        We use only <strong>strictly necessary</strong> cookies — no advertising, no third-party tracking, no analytics
                        tied to your identity:
                    </p>
                    <ul className="list-disc list-outside space-y-2 ml-5">
                        <li>A session cookie that keeps you signed in (expires when you sign out or after inactivity)</li>
                        <li>A CSRF protection token (prevents cross-site request forgery on form submissions)</li>
                    </ul>
                    <p>That's it. No Google Analytics, no Facebook Pixel.</p>
                </Section>

                <Section icon={Shield} title="How we protect your data" id="security">
                    <ul className="list-disc list-outside space-y-2 ml-5">
                        <li>All connections to our site use TLS (HTTPS) — your data is encrypted in transit</li>
                        <li>Passwords are hashed using bcrypt — even our admins cannot read your password</li>
                        <li>The database is encrypted at rest by our cloud provider</li>
                        <li>Admin and board accounts are protected by two-factor authentication (FIDO2 security keys / passkeys)</li>
                        <li>We run nightly backups of the database, retained for 14 days, then deleted</li>
                        <li>Every board permission change is recorded in an audit log</li>
                    </ul>
                    <p className="mt-4">
                        If a data breach affects you, we will notify you by email <strong>within 72 hours</strong> of discovery,
                        per industry best practice.
                    </p>
                </Section>

                <Section icon={Trash2} title="Your rights and choices" id="your-rights">
                    <p>You can, at any time:</p>
                    <ul className="list-disc list-outside space-y-2 ml-5">
                        <li>
                            <strong>See what we have about you</strong> — sign in and visit your donor portal, or email
                            us and we will send a complete export within 30 days.
                        </li>
                        <li>
                            <strong>Correct any error</strong> — update your name or email in the donor portal at any time.
                        </li>
                        <li>
                            <strong>Delete your account</strong> — sign in → <Link to="/account" className="text-[#E53935] hover:underline">Account settings</Link> → "Delete my account". This is permanent and removes your profile,
                            sign-in history, and (where legally allowed) your donation history.
                            <span className="block mt-1 text-sm">
                                Note: U.S. tax law requires us to retain a record of donations for 7 years, so the dollar amounts and dates remain
                                in our books for audit purposes, but stripped of your name and email.
                            </span>
                        </li>
                        <li>
                            <strong>Opt out of all email</strong> — every email we send has an unsubscribe link. You'll still
                            get transactional emails (receipts, password resets) because they're required.
                        </li>
                        <li>
                            <strong>Revoke a social-login</strong> — visit your Google / Facebook account settings and remove
                            Reddog Foundation from connected apps. You can still sign in with email + password.
                        </li>
                    </ul>
                </Section>

                <Section icon={FileText} title="Children's privacy (FERPA)" id="ferpa">
                    <p>
                        We work with K-12 students through partner schools. We do not knowingly collect personal data from
                        children under 13 directly. When images of identifiable students appear on our site, we require a
                        signed photo-release form on file from a parent or guardian (tracked in our internal image library
                        with consent metadata). Parents may revoke consent at any time by emailing the foundation, and we
                        will remove the image from all public surfaces within 5 business days.
                    </p>
                </Section>

                <Section icon={AlertCircle} title="Changes to this policy" id="changes">
                    <p>
                        We may update this policy as the foundation grows or laws change. Material changes will be announced
                        on the homepage and emailed to active donors at least 30 days before they take effect. The "Last updated"
                        date above always reflects the most recent revision.
                    </p>
                </Section>

                <Section icon={Mail} title="Contact us" id="contact">
                    <p>
                        Questions, complaints, or data requests:
                    </p>
                    <p className="font-medium text-[#1C252E]">
                        Reddog Community Foundation<br />
                        Email: <a href={`mailto:${privacyEmail}`} className="text-[#E53935] hover:underline">{privacyEmail}</a>
                    </p>
                    <p>
                        We respond to data requests within 30 days.
                    </p>
                </Section>
            </div>

            <p className="text-xs text-[#4A5568] italic mt-12 pt-6 border-t border-black/10">
                This policy is provided in good faith for transparency. It is not legal advice. The foundation
                is currently in the process of applying for 501(c)(3) status; we follow standard nonprofit data
                handling practices in the interim.
            </p>
        </div>
    </div>
    );
};

export default Privacy;
